import { useMemo } from 'react';
import moment from 'moment';
import { useSelector, shallowEqual } from 'react-redux';

import { useEmployee } from 'components/direct-sales/order-summary/hooks/useEmployee';
import { useApplicant } from 'components/direct-sales/order-summary/hooks/useApplicant';
import { useAddresses } from 'components/direct-sales/order-summary/hooks/useAddresses';
import { useElectricityMeterPoints } from 'components/direct-sales/order-summary/hooks/useElectricityMeterPoints';
import { useGasMeterPoints } from 'components/direct-sales/order-summary/hooks/useGasMeterPoints';
import { usePsr } from 'components/direct-sales/order-summary/hooks/usePsr';
import { usePaymentPayload } from 'components/direct-sales/order-summary/hooks/usePaymentPayload';
import { useSalesSubChannel } from 'components/direct-sales/order-summary/hooks/useSalesSubChannel';
import { useSalesChannel } from 'components/direct-sales/order-summary/hooks/useSalesChannel';
import { useFmrsPayload } from 'components/direct-sales/order-summary/hooks/useFmrsPayload';
import { useSalesType } from './useSalesType';
import { useReferFriend } from 'hooks/useReferFriend';
import { useCreditValue } from './useCreditValue';
import { DATE_FORMAT } from 'config/app-config';
import { selectDetails as selectAccount } from 'state/account/account-slice';
import { selectElectricVehicle } from 'state/electric-vehicle/electric-vehicle-slice';
import { selectPeakSaveConsent } from 'state/peak-save/peak-save-slice';

export const useSalesOrder = () => {
  const today = moment().format(DATE_FORMAT);
  const { salesAgentCode } = useEmployee();
  const applicant = useApplicant();
  const { rafToken } = useReferFriend();
  const addresses = useAddresses();
  const electricityMeterPoints = useElectricityMeterPoints();
  const gasMeterPoints = useGasMeterPoints();
  const psr = usePsr();
  const paymentMethod = usePaymentPayload();
  const salesType = useSalesType();
  const salesSubChannel = useSalesSubChannel();
  const salesChannel = useSalesChannel();
  const fmrs = useFmrsPayload();
  const voiceSaleCredit = useCreditValue();
  const { relationshipToProperty } = useSelector(selectAccount, shallowEqual);
  const customerHaveAnElectricVehicle = useSelector(
    selectElectricVehicle,
    shallowEqual
  );
  const hasPeakSaveConsent = useSelector(selectPeakSaveConsent, shallowEqual);

  return useMemo(
    () => ({
      applicant,
      addresses,
      customerHaveAnElectricVehicle,
      customerPeakSaveRegistrationRequest: hasPeakSaveConsent,
      ...electricityMeterPoints,
      ...gasMeterPoints,
      ...psr,
      ...salesChannel,
      ...salesSubChannel,
      ...salesType,
      ...(salesAgentCode && { salesAgentCode }),
      ...fmrs,
      ...(relationshipToProperty && { relationshipToProperty }),
      paymentMethod,
      signatureConfirmation: true,
      signatureDate: today,
      buyapowa: rafToken ? { token: encodeURIComponent(rafToken) } : {},
      ...voiceSaleCredit
    }),
    [
      addresses,
      applicant,
      customerHaveAnElectricVehicle,
      electricityMeterPoints,
      gasMeterPoints,
      paymentMethod,
      psr,
      salesAgentCode,
      salesChannel,
      salesSubChannel,
      salesType,
      fmrs,
      today,
      rafToken,
      voiceSaleCredit,
      relationshipToProperty,
      hasPeakSaveConsent
    ]
  );
};
